import Header from '@amzn/awsui-components-react/polaris/header';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import {
    useInterval,
    useLazyRequest,
    useVisibilityChange,
} from '@amzn/event-engine-js-utils';
import {
    StoragePartition,
    StoragePartitionKeys,
} from '@amzn/event-engine-js-utils';
import { state as breadcrumbsState } from 'atoms/breadcrumbs';
import { AWSError } from 'aws-sdk/lib/error';
import SpinnerWithLabel from 'components/SpinnerWithLabel';
import { differenceInMinutes } from 'date-fns';
import { useEventParticipant, useNotification } from 'hooks';
import React, {
    FC,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import EEEvents from 'services/ee-events';
import ErrorMessage, { ErrorCode, MessageTypes } from 'services/error-message';
import { DEFAULT_POLL_MS } from 'utils/config';
import { i18nStrings } from 'utils/constants';

import { routes } from '../../constants';
import EventDurationMessage from './components/EventDurationMessage';
import EventInformationContainer from './components/EventInformationContainer';
import WorkshopInformationContainer from './components/WorkshopInformationContainer';
import {
    BASE_BREADCRUMBS,
    EVENT_DURATION_INTERVAL,
    MAX_CONSECUTIVE_ERROR_THRESHOLD,
    NOTIFICATION_THRESHOLDS_MINUTES,
} from './constants';
import EventDashboard from './EventDashboard';
import { HistoryState, NotificationID, StorageKeyID, TestID } from './types';
import compareEventState from './utils/compare-event-state';

const EventDashboardContainer: FC = () => {
    const history = useHistory<HistoryState>();
    const setBreadcrumbs = useSetRecoilState(breadcrumbsState);
    const { getParticipant, setParticipant } = useEventParticipant();
    const errorCount = useRef(0);
    const eventDurationHideTime = useRef(
        StoragePartition.getItem(
            StoragePartitionKeys.userPreferences,
            StorageKeyID.EVENT_DURATION_HIDE_TIME
        )
    );
    const [isIntervalPaused, setIsIntervalPaused] = useState(false);
    const [participantId, setParticipantId] = useState<string | null>();
    const [getEvent, { data, isError: eventDataError }] = useLazyRequest<
        typeof EEEvents.getParticipantEvent,
        AWSError
    >(EEEvents.getParticipantEvent, {});
    const { showNotification, clearNotification } = useNotification();
    const eventData = useMemo(() => data, [data?.stateRuntime]);
    /**
     * Show generic event details page without content if:
     * - The event has not started
     * - The event is paused
     * - The event is terminated
     */
    const isContentVisible = useMemo(
        () =>
            eventData &&
            !(
                compareEventState(eventData.stateRuntime, 'start_success') <
                    0 ||
                eventData.stateRuntime === 'pause_success' ||
                compareEventState(
                    eventData.stateRuntime,
                    'terminate_success'
                ) >= 0
            ),
        [eventData?.stateRuntime]
    );
    const dashboard = useMemo(
        () =>
            eventData && participantId ? (
                <EventDashboard
                    eventData={eventData}
                    participantId={participantId}
                />
            ) : null,
        [eventData, participantId]
    );
    const getParticipantEventInfo = useCallback(() => {
        participantId && getEvent(participantId);
    }, [participantId]);
    const isEventDurationNotificationVisible = (endDate: Date) => {
        if (!eventDurationHideTime.current) {
            return true;
        }

        const hideTime = new Date(parseInt(eventDurationHideTime.current, 10));
        const now = Date.now();

        for (
            let i = 0, len = NOTIFICATION_THRESHOLDS_MINUTES.length;
            i < len;
            i++
        ) {
            const minuteThreshold = NOTIFICATION_THRESHOLDS_MINUTES[i];

            if (
                differenceInMinutes(endDate, now) <= minuteThreshold &&
                differenceInMinutes(endDate, hideTime) > minuteThreshold
            ) {
                return true;
            }
        }

        return false;
    };

    useEffect(() => {
        if (participantId === undefined) {
            return;
        }

        if (!participantId) {
            showNotification(
                i18nStrings.event.notifications.noCurrentParticipant.content
            );
            history.push(`${routes.joinEvent}`);
            return;
        }

        getParticipantEventInfo();
    }, [getParticipantEventInfo]);

    useEffect(() => {
        if (!data) {
            return;
        }

        errorCount.current = 0;
        setIsIntervalPaused(false);

        let notificationArgs:
            | Parameters<typeof showNotification>
            | undefined = undefined;

        if (data.stateRuntime === 'not_started') {
            notificationArgs = [
                i18nStrings.event.notifications.eventNotStarted.content,
                {
                    header:
                        i18nStrings.event.notifications.eventNotStarted.header,
                    dismissible: false,
                },
            ];
        } else if (data.stateRuntime === 'pause_success') {
            notificationArgs = [
                i18nStrings.event.notifications.eventPaused.content,
                {
                    header: i18nStrings.event.notifications.eventPaused.header,
                    dismissible: false,
                },
            ];
        } else if (
            compareEventState(data.stateRuntime, 'start_success') >= 0 &&
            isEventDurationNotificationVisible(data.endTime)
        ) {
            notificationArgs = [
                <EventDurationMessage
                    key={NotificationID.EventState}
                    eventData={data}
                    updateIntervalMS={EVENT_DURATION_INTERVAL}
                />,
                {
                    dismissible: true,
                    onDismiss: () => {
                        const now = Date.now();
                        StoragePartition.setItem(
                            StoragePartitionKeys.userPreferences,
                            now,
                            StorageKeyID.EVENT_DURATION_HIDE_TIME
                        );
                        eventDurationHideTime.current = now;
                    },
                },
            ];
        }

        if (notificationArgs) {
            showNotification(notificationArgs[0], {
                ...notificationArgs[1],
                id: NotificationID.EventState,
                clearStack: false,
            });
        } else {
            clearNotification(NotificationID.EventState);
        }
    }, [data]);

    useEffect(() => {
        (!eventData || eventData.stateRuntime === 'pause_success') &&
            setBreadcrumbs(BASE_BREADCRUMBS);
    }, [eventData]);

    useEffect(() => {
        if (!eventDataError) {
            clearNotification(NotificationID.EventDataError);
            return;
        }

        // Do not surface critical server errors that come from event
        // data poller since it's disruptive to the event experience,
        // and may cause unnecessary confusion.
        if (eventDataError.statusCode && eventDataError.statusCode < 500) {
            showNotification(
                ErrorMessage.getMessage(
                    eventDataError,
                    MessageTypes.getParticipantEvent
                ),
                {
                    id: NotificationID.EventDataError,
                    clearStack: false,
                }
            );
        }

        // Errors from inbound API responses will contain status codes.
        // Other errors from requests that are never sent to the server
        // (e.g. NetworkingError) will not add to this count.
        if (eventDataError.statusCode) {
            errorCount.current += 1;
        }

        if (errorCount.current >= MAX_CONSECUTIVE_ERROR_THRESHOLD) {
            setIsIntervalPaused(true);
        }

        if (eventDataError.code === ErrorCode.NotAuthorizedException) {
            setParticipant(null);
            history.push(`${routes.landingPage}`);
        }
    }, [eventDataError]);

    useEffect(() => {
        if (history.location.state?.participantId) {
            setParticipant(history.location.state.participantId);
            window.history.replaceState({}, document.title);
        }

        setParticipantId(getParticipant());

        return () => {
            clearNotification(NotificationID.EventState);
        };
    }, []);

    useInterval(
        getParticipantEventInfo,
        !isIntervalPaused && participantId ? DEFAULT_POLL_MS : null
    );

    useVisibilityChange({
        onHidden: () => {
            setIsIntervalPaused(true);
        },
        onVisible: () => {
            setIsIntervalPaused(false);
        },
    });

    if (!(eventData && participantId)) {
        return (
            <SpinnerWithLabel>
                {i18nStrings.event.loadingDashboard}
            </SpinnerWithLabel>
        );
    }

    if (!isContentVisible) {
        return (
            <SpaceBetween
                data-testid={TestID.EventOverview}
                direction="vertical"
                size="m">
                <Header variant="h1">{eventData.title}</Header>
                <SpaceBetween direction="vertical" size="l">
                    <EventInformationContainer eventData={eventData} />
                    <WorkshopInformationContainer
                        contentSummary={eventData.contentSummary}
                        isContentVisible={false}
                    />
                </SpaceBetween>
            </SpaceBetween>
        );
    }

    return dashboard;
};

export default EventDashboardContainer;
